import { message, Modal } from 'antd';
import { FormInstance } from 'antd/lib/form';
import {
  team_create,
  team_delete,
  team_deleteByAdmin,
  team_deleteMember,
  team_deleteUserFromTask,
  team_updateMember,
  team_updateName,
  team_changeLeader,
  team_updateMemberRole,
  team_getMemberNotInTeam,
} from '../../../services/team';
import { Team, AvailableMember } from '../types';

interface UseTeamActionsProps {
  taskId: string;
  form: FormInstance;
  onSuccess: () => void;
  isPrivilegedUser?: boolean;
  isTaskAdmin?: boolean;
}

export const useTeamActions = ({ taskId, form, onSuccess, isPrivilegedUser, isTaskAdmin }: UseTeamActionsProps) => {
  const saveTeam = async (editingTeam: Team | null, teamConfig?: { min_size: number; team_size: number; teamname_regex: string | null }) => {
    try {
      const values = await form.validateFields();
      console.log('=== Form Values ===', values);

      // ตรวจสอบจำนวนสมาชิก (ยกเว้น task admin)
      const memberCount = values.members?.length || 0;
      const minTeamSize = teamConfig?.min_size ?? 1;
      const maxTeamSize = teamConfig?.team_size ?? 15;

      if (!isTaskAdmin && memberCount < minTeamSize) {
        message.error(`จำนวนสมาชิกต้องมีอย่างน้อย ${minTeamSize} คน`);
        return;
      }

      if (!isTaskAdmin && memberCount > maxTeamSize) {
        message.error(`จำนวนสมาชิกต้องไม่เกิน ${maxTeamSize} คน`);
        return;
      }

      if (editingTeam) {
        // กรณีพิเศษ: ตรวจสอบก่อนว่าหลังจากลบสมาชิกแล้วไม่มีคนเหลืออยู่เลย → ลบทีมทิ้งเลย
        const remainingMembersCount = values.members?.length || 0;

        if (remainingMembersCount === 0) {
          const deletePayload = {
            task_id: taskId,
            team_id: editingTeam.id,
          };
          console.log('=== Delete Team (ไม่มีสมาชิกเหลืออยู่) ===', deletePayload);

          const deleteResult = await team_delete(deletePayload);
          if (deleteResult.status === 'success') {
            message.success('ลบทีมสำเร็จ');
            onSuccess();
            return;
          } else {
            message.error('ไม่สามารถลบทีมได้');
            return;
          }
        }

        // ตรวจสอบว่ามี Leader เพียงคนเดียว
        const leaderCount =
          values.members?.filter((m: any) => m.role === 1).length || 0;
        if (leaderCount !== 1) {
          message.error('ทีมต้องมีหัวหน้าเพียงคนเดียวเท่านั้น');
          return;
        }

        // Update team via API
        const promises: Promise<any>[] = [];

        // 1. Find removed and added members FIRST
        const currentMemberIds = editingTeam.members.map((m) => m.id);
        const newMemberIds =
          values.members?.map((m: any) => m.user_id || m.id) || [];
        const removedMemberIds = currentMemberIds.filter(
          (id) => !newMemberIds.includes(id)
        );
        const addedMemberIds = newMemberIds.filter(
          (id: string) => !currentMemberIds.includes(id)
        );

        // 2. ตรวจสอบการเปลี่ยน Leader
        const oldLeader = editingTeam.members.find((m) => m.role === 1);
        const newLeader = values.members?.find((m: any) => m.role === 1);

        // ตรวจสอบว่าหัวหน้าคนเก่าถูกลบออกหรือไม่
        const isOldLeaderRemoved = oldLeader && removedMemberIds.includes(oldLeader.id);

        // 3. Update team name if changed
        if (values.name !== editingTeam.name) {
          const namePayload = {
            task_id: taskId,
            team_id: editingTeam.id,
            team_name: values.name,
          };
          console.log('=== Update Team Name Payload ===', namePayload);
          promises.push(team_updateName(namePayload));
        }

        // 4. Delete removed members (ลบก่อน)
        for (const userId of removedMemberIds) {
          const deletePayload = {
            task_id: taskId,
            team_id: editingTeam.id,
            user_id: userId,
          };
          console.log('=== Delete Member Payload ===', deletePayload);
          promises.push(team_deleteMember(deletePayload));
        }

        // 5. Add new members (เพิ่มก่อน)
        if (addedMemberIds.length > 0) {
          const updateMemberPayload = {
            task_id: taskId,
            team_id: editingTeam.id,
            user_id: addedMemberIds,
          };
          console.log('=== Add Members Payload ===', updateMemberPayload);
          promises.push(team_updateMember(updateMemberPayload));
        }

        // Execute all API calls FIRST (ลบและเพิ่มสมาชิก + เปลี่ยนชื่อทีม)
        const results = await Promise.all(promises);
        const allSuccess = results.every((r) => r.status === 'success');

        if (!allSuccess) {
          const failedResult = results.find((r) => r.status !== 'success');
          message.error(failedResult?.message || 'ไม่สามารถอัพเดททีมได้บางส่วน');
          onSuccess();
          return;
        }

        // 6. เปลี่ยนหัวหน้าทีม
        if (
          oldLeader &&
          newLeader &&
          oldLeader.id !== (newLeader.user_id || newLeader.id)
        ) {
          if (isOldLeaderRemoved) {
            // กรณีหัวหน้าคนเก่าถูกลบออก: ตั้งหัวหน้าคนใหม่โดยตรง
            const updateRolePayload = {
              task_id: taskId,
              team_id: editingTeam.id,
              user_id: newLeader.user_id || newLeader.id,
              team_role: 1, // Leader
            };
            console.log('=== Update Member Role to Leader Payload ===', updateRolePayload);

            const updateRoleResult = await team_updateMemberRole(updateRolePayload);
            if (updateRoleResult.status !== 'success') {
              message.error('ไม่สามารถตั้งหัวหน้าทีมได้');
              onSuccess(); // Refresh เพื่อให้ข้อมูลตรงกับ DB
              return;
            }
          } else {
            // กรณีปกติ: สลับ role ระหว่างหัวหน้าเก่าและใหม่
            const changeLeaderPayload = {
              task_id: taskId,
              team_id: editingTeam.id,
              leader_user_id: oldLeader.id,
              member_user_id: newLeader.user_id || newLeader.id,
            };
            console.log('=== Change Leader Payload ===', changeLeaderPayload);

            const changeLeaderResult = await team_changeLeader(changeLeaderPayload);
            if (changeLeaderResult.status !== 'success') {
              message.error('ไม่สามารถเปลี่ยนหัวหน้าทีมได้');
              onSuccess(); // Refresh เพื่อให้ข้อมูลตรงกับ DB
              return;
            }
          }
        }

        // Success - refresh team list
        message.success('อัพเดททีมสำเร็จ');
        onSuccess();
      } else {
        const leaderCount =
          values.members?.filter((m: any) => m.role === 1).length || 0;
        if (leaderCount !== 1) {
          message.error('ทีมต้องมีหัวหน้าเพียงคนเดียวเท่านั้น');
          return;
        }

        const memberUserIds =
          values.members?.map((m: any) => m.user_id || m.id) || [];

        const payload = {
          task_id: taskId,
          team_name: values.name,
          user_id: memberUserIds,
        };
        const response = await team_create(payload);

        if (response.status === 'success') {
          message.success('สร้างทีมสำเร็จ');
          onSuccess();
        } else {
          message.error('ไม่สามารถสร้างทีมได้ โปรดตรวจสอบว่าคุณมีทีมอยู่แล้วหรือลงทะเบียนเข้างานแล้วหรือไม่');
        }
      }
    } catch (error) {
      console.error('Error saving team:', error);
      message.error('เกิดข้อผิดพลาดในการบันทึกข้อมูล');
    }
  };

  const deleteTeam = async (id: string, onDeleteSuccess: () => void) => {
    try {
      Modal.confirm({
        title: 'ยืนยันการลบทีม',
        content: 'คุณต้องการลบทีมนี้หรือไม่?',
        okText: 'ลบ',
        cancelText: 'ยกเลิก',
        okType: 'danger',
        onOk: async () => {
          try {
            const payload = {
              task_id: taskId,
              team_id: id,
            };

            console.log('=== Delete Team Payload ===', payload);

            const response = isPrivilegedUser
              ? await team_deleteByAdmin(payload)
              : await team_delete(payload);

            if (response.status === 'success') {
              message.success('ลบทีมสำเร็จ');
              onDeleteSuccess();
            } else {
              message.error(response.message || 'ไม่สามารถลบทีมได้');
            }
          } catch (error) {
            console.error('Error deleting team:', error);
            message.error('เกิดข้อผิดพลาดในการลบทีม');
          }
        },
      });
    } catch (error) {
      console.error('Error showing confirmation:', error);
    }
  };

  const fetchAvailableMembers = async (): Promise<AvailableMember[]> => {
    try {
      const response = await team_getMemberNotInTeam({
        task_id: taskId,
      });

      console.log('Available members response:', response);

      if (response.status === 'success' && response.data) {
        return response.data;
      }
      return [];
    } catch (error) {
      console.error('Error fetching members:', error);
      message.error('ไม่สามารถดึงข้อมูลสมาชิกได้');
      return [];
    }
  };

  const leaveTeam = async (teamId: string, userId: string, onLeaveSuccess: () => void) => {
    try {
      Modal.confirm({
        title: 'ยืนยันการออกจากทีม',
        content: 'คุณต้องการออกจากทีมนี้หรือไม่?',
        okText: 'ออกจากทีม',
        cancelText: 'ยกเลิก',
        okType: 'danger',
        onOk: async () => {
          try {
            const payload = {
              task_id: taskId,
              team_id: teamId,
              user_id: userId,
            };

            console.log('=== Leave Team Payload ===', payload);

            const response = await team_deleteMember(payload);

            if (response.status === 'success') {
              message.success('ออกจากทีมสำเร็จ');
              onLeaveSuccess();
            } else {
              message.error(response.message || 'ไม่สามารถออกจากทีมได้');
            }
          } catch (error) {
            console.error('Error leaving team:', error);
            message.error('เกิดข้อผิดพลาดในการออกจากทีม');
          }
        },
      });
    } catch (error) {
      console.error('Error showing confirmation:', error);
    }
  };

  const removeMemberByAdmin = async (userId: string, onDone: () => void) => {
    Modal.confirm({
      title: 'ยืนยันการลบสมาชิก',
      content: 'คุณต้องการลบสมาชิกคนนี้ออกจาก task หรือไม่?',
      okText: 'ลบ',
      cancelText: 'ยกเลิก',
      okType: 'danger',
      onOk: async () => {
        try {
          const response = await team_deleteUserFromTask({ task_id: taskId, user_id: userId });
          if (response.status === 'success') {
            message.success('ลบสมาชิกออกจาก task สำเร็จ');
            onDone();
          } else {
            message.error(response.message || 'ไม่สามารถลบสมาชิกได้');
          }
        } catch (error) {
          message.error('เกิดข้อผิดพลาดในการลบสมาชิก');
        }
      },
    });
  };

  return {
    saveTeam,
    deleteTeam,
    leaveTeam,
    removeMemberByAdmin,
    fetchAvailableMembers,
  };
};
